<!-- What a PDF is doing, in front of everything else.
  --
  -- This started as a card in the page and that was the wrong shape: on a
  -- laptop it sat below the fold, so the one moment the visitor genuinely
  -- needs feedback was the one moment they could not see any. Rasterising is
  -- also the only thing in this application they cannot work around or
  -- retry -- it either finishes or it does not -- so it earns taking over the
  -- screen.
  --
  -- Two phases, and only one of them can be predicted.
  --
  --   Uploading   real bytes, real rate, so a real bar and a real estimate.
  --   Rendering   the server counts pages as it makes them and again as it
  --               stores them, and answers /pdf-progress while it works, so
  --               this is a real bar and a real "12 of 40". Where nothing
  --               answers -- an old browser, a dropped poll, a server that
  --               predates the endpoint -- it falls back to the estimate from
  --               the page count, dropped rather than stretched once spent.
  --
  -- There is a cancel, because a modal with no way out is a trap. During the
  -- upload it genuinely stops the work. During rendering it stops the waiting;
  -- the server finishes regardless and the sweep collects the pages.
  -->
<template>
    <div v-if="uiStore.pdfPhase" class="fixed inset-0 z-50 flex items-center justify-center p-4"
         style="background: rgba(11, 9, 20, 0.72)"
         role="dialog" aria-modal="true" :aria-label="title">

        <div class="w-full max-w-md rounded-2xl border border-rule bg-raised p-7 flex flex-col items-center gap-5 text-center shadow-2xl">

            <!-- A ring rather than the small inline spinner: this is the only
              -- thing on screen, so it can afford to be the size of the thing
              -- it is reporting on. -->
            <svg class="h-16 w-16 animate-spin text-brand" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="4" class="opacity-20"></circle>
                <path d="M44 24a20 20 0 0 0-20-20" stroke="currentColor" stroke-width="4" stroke-linecap="round"></path>
            </svg>

            <div class="flex flex-col gap-1.5">
                <h2 class="text-xl font-black tracking-tight">{{title}}</h2>
                <!-- WHICH document. On /pdf-to-jpg the conversion starts on its
                  -- own, so this is where the visitor confirms they picked the
                  -- right file -- and a PDF is the one input with no thumbnail,
                  -- which makes the name the only thing there is to check. It
                  -- is above the page count because a wrong file is a wrong
                  -- NAME first; the count is corroboration.
                  --
                  -- break-all, because these are real filenames: the report
                  -- this was written against was "La Repubblica Robinson N.509
                  -- - 06 Settembre 2026.pdf". -->
                <p v-if="docName" class="text-[15px] font-bold break-all leading-snug">{{docName}}</p>
                <p v-if="uiStore.pdfPages" class="text-[15px] text-ink-mute">
                    {{ t('pdf.pageCount', { n: uiStore.pdfPages }) }}
                </p>
            </div>

            <!-- Waiting for a slot. Nothing to measure, so no bar: a
              -- countdown and the reason, which is all there is to say. -->
            <p v-if="uiStore.pdfPhase === 'waiting'" class="text-[15px] text-ink-soft">
                {{ uiStore.pdfRetryIn
                     ? t('pdf.busyRetry', { n: uiStore.pdfRetryIn })
                     : t('pdf.busyNote') }}
            </p>

            <!-- Only while there is something real to measure. -->
            <div v-if="uiStore.pdfPhase === 'uploading'" class="w-full flex flex-col gap-2">
                <div class="h-2 w-full overflow-hidden rounded-full bg-sunk">
                    <div class="h-full rounded-full bg-brand transition-[width] duration-200"
                         :style="`width: ${uiStore.pdfPercent}%`"></div>
                </div>
                <div class="flex items-baseline justify-between text-sm text-ink-mute">
                    <span class="tabular-nums">{{uiStore.pdfPercent}}%</span>
                    <span v-if="remaining" class="tabular-nums">{{remaining}}</span>
                </div>
            </div>

            <!-- Rendering, and there IS progress to report now: the server
              -- counts the pages as it makes them and again as it stores them,
              -- and answers /pdf-progress while it works. A real bar and a real
              -- "12 of 40".
              --
              -- The estimate is the fallback, not the plan: an old browser, a
              -- dropped poll or a server that has not been updated all leave
              -- pdfTotal at zero, and then this is exactly what it was. -->
            <div v-else class="w-full flex flex-col gap-2">
                <template v-if="counted">
                    <div class="h-2 w-full overflow-hidden rounded-full bg-sunk">
                        <div class="h-full rounded-full bg-brand transition-[width] duration-200"
                             :style="`width: ${countedPercent}%`"></div>
                    </div>
                    <div class="flex items-baseline justify-between text-sm text-ink-mute">
                        <span class="tabular-nums">{{ t('pdf.pagesDone', {done: uiStore.pdfDone, total: uiStore.pdfTotal}) }}</span>
                        <span class="tabular-nums">{{countedPercent}}%</span>
                    </div>
                </template>
                <p class="text-[15px] text-ink-soft">{{ t('pdf.renderingNote') }}</p>
                <p v-if="!counted" class="text-sm tabular-nums text-ink-mute">
                    {{ remaining || t('pdf.stillWorking') }}
                </p>
            </div>

            <button @click.prevent="$emit('cancel')"
                    class="btn btn-sm btn-ghost rounded-lg text-ink-mute">{{ t('pdf.cancel') }}</button>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "../store/uiElements"

defineEmits(["cancel"])
const uiStore = useUiElements()
const { t } = useI18n()

/* The staged document's name, straight off the store rather than a prop.
 *
 * It survives the whole run -- startPdf() does not clear uiStore.files -- so
 * the name is on screen for the upload, the wait for a slot and the rendering,
 * which is exactly the window in which cancelling still means something. */
const docName = computed(() => {
    const f = uiStore.files && uiStore.files[0]
    return (f && f.file && f.file.name) || ""
})

const title = computed(() => {
    // The server's slots are full and it said so with a 503 and a Retry-After.
    // Nothing is wrong with the document, so this is a wait and not an error --
    // App.vue retries by itself and this is what the visitor reads meanwhile.
    if (uiStore.pdfPhase === "waiting") return t("pdf.busy")
    if (uiStore.pdfPhase === "uploading") return t("pdf.uploading")
    // Two phases on the server, and they take comparable time on a long
    // document -- so which one is running is worth saying rather than calling
    // the whole wait "rendering".
    //
    // The second one is named for what the visitor is waiting FOR, not for what
    // the server is doing. It puts each page where the browser can fetch it,
    // which is storage, and everywhere else in the copy that word travels with
    // the clause that makes it honest -- a random unguessable address, deleted
    // automatically. A progress title has no room for the clause, and the bare
    // verb over a progress bar reads as the point of the exercise rather than a
    // step on the way to a download.
    return uiStore.pdfStage === "storing" ? t("pdf.preparing") : t("pdf.rendering")
})

/* Whether the server is telling us how far along it is. */
const counted = computed(() => uiStore.pdfPhase !== "uploading" && uiStore.pdfTotal > 0)

/* Both phases are per page and take comparable time on a long document, so the
 * bar covers them as two halves rather than filling up and starting again. */
const countedPercent = computed(() => {
    if (!counted.value) return 0
    const half = Math.min(100, Math.round((uiStore.pdfDone / uiStore.pdfTotal) * 100))
    return uiStore.pdfStage === "storing" ? 50 + Math.round(half / 2) : Math.round(half / 2)
})

/* Seconds left, or "" when there is no honest number to give.
 *
 * Uploading: bytes remaining over the rate measured so far.
 * Rendering: the estimate from the page count, minus what has already gone.
 *
 * Either way, once it would reach zero it stops being shown. A countdown stuck
 * at "1 s left" is worse than no countdown, because it says the thing is
 * broken rather than that it is slow. */
const remaining = computed(() => {
    const ms = uiStore.pdfRemainingMs
    if (!ms || ms <= 0) return ""
    return t("pdf.remaining", { n: Math.max(1, Math.ceil(ms / 1000)) })
})
</script>
